import { useContext, type FC, type PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import DemoForm from '@segment/templates/Forms/DemoForm/'
import ProgressiveProfileForm from '@segment/templates/Forms/ProgressiveProfileForm'

import { DemoContext } from '@segment/contexts/demo'

import AlreadyHaveAWorkspace from '../AlreadyHaveAWorkspace/AlreadyHaveAWorkspace'
import LogoSection from '../LogoSection'
import WantATour from '../WantATour'

interface HeroProps {
  beginsWithLeadEnrichmentForm?: boolean
}

const Hero: FC<PropsWithChildren<HeroProps>> = ({ beginsWithLeadEnrichmentForm }) => {
  const { completedFormData } = useContext(DemoContext)

  const copy = {
    overline: `Get a demo`,
    heading: beginsWithLeadEnrichmentForm
      ? `Help us get you to the right starting point`
      : `See what Segment can do for you`,
    description: beginsWithLeadEnrichmentForm
      ? `Answer 4 more questions to help us pinpoint exactly what your team needs to get started with Segment.`
      : `Tell us a little about yourself and we'll connect you with a Segment expert who can share more about the product and answer any questions you have.`,
  }

  // Copy that goes before the form and is hidden after form submission
  const beforeContent = (
    <>
      {!beginsWithLeadEnrichmentForm && (
        <CopyText.Overline tag="h1" variant="h5">
          {copy.overline}
        </CopyText.Overline>
      )}
      <CopyText.Heading variant="h1" tag="h2">
        {copy.heading}
      </CopyText.Heading>
      <CopyText.Description variant="pMedium" tag="p">
        {copy.description}
      </CopyText.Description>
    </>
  )

  return (
    <Wrapper>
      <HeroBase variant="2Col">
        <HeroRecipe.Copy>
          {beginsWithLeadEnrichmentForm ? (
            <ProgressiveProfileForm before={beforeContent} />
          ) : (
            <DemoForm before={beforeContent}></DemoForm>
          )}
          {completedFormData && <AlreadyHaveAWorkspace />}
        </HeroRecipe.Copy>

        <StyledContents>{completedFormData ? <WantATour /> : <LogoSection />}</StyledContents>
      </HeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  align-items: flex-start;

  ${HeroRecipe.Wrapper} {
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      grid-template-columns: 1fr 1fr;
    }
    ${({ theme }) => theme.breakpoints.up(`xl`)} {
      gap: 200px;
    }
  }
`

const StyledContents = styled(HeroRecipe.Contents)`
  justify-items: stretch;
  width: auto;
`

export default Hero
